import { useEffect, useState, useCallback } from "react";
import {
  getPermisos,
  createPermiso,
  updatePermiso
} from "../../../services/admin/permisos_admin";
import PermisoFormModal from "./PermisoFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";

function PermisosPage() {
  const [permisos, setPermisos] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedPermiso, setSelectedPermiso] = useState(null);

  const cargarPermisos = useCallback(async () => {
    try {
      const data = await getPermisos();
      setPermisos(data);
      setTotalPages(Math.ceil(data.length / pageSize));
    } catch (error) {
      console.error("Error loading permissions:", error);
    }
  }, [pageSize]);

  useEffect(() => {
    cargarPermisos();
  }, [cargarPermisos]);

  // Filter search
  const filtered = permisos.filter((p) =>
    p.descripcion.toLowerCase().includes(search.toLowerCase())
  );

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const nextPage = () => page < totalPages && setPage(page + 1);
  const prevPage = () => page > 1 && setPage(page - 1);

  const openCreateModal = () => {
    setSelectedPermiso(null);
    setShowModal(true);
  };

  const openEditModal = (permiso) => {
    setSelectedPermiso(permiso);
    setShowModal(true);
  };

  const handleCreate = async (data) => {
    await createPermiso(data);
    cargarPermisos();
    setShowModal(false);
  };

  const handleUpdate = async (id, data) => {
    await updatePermiso(id, data);
    cargarPermisos();
    setShowModal(false);
  };

  return (
    <div className="page-container">
      <style>{modernStyles}</style>

      {/* HEADER */}
      <div className="header">
        <h2 className="title">Permissions Management</h2>

        <div className="top-bar">
          <input
            type="text"
            placeholder="Search permission..."
            className="search-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

          {hasPerm("Create Permission") && <button className="btn-create" onClick={openCreateModal}>
            Create Permission
          </button>
          }
        </div>
      </div>

      {/* TABLE */}
      <div className="table-card">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Description</th>
              <th style={{ textAlign: "right" }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan="3" className="no-data">
                  No permissions found
                </td>
              </tr>
            ) : (
              paginated.map((p) => (
                <tr key={p.permiso_id}>
                  <td>{p.permiso_id}</td>
                  <td>{p.descripcion}</td>

                  <td>
                  <div className="actions">
                        {hasPerm("Update permission") && <span
                        className="action-icon edit"
                        onClick={() => openEditModal(p)}
                      >
                        {IconEdit}
                      </span>
                      }
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {showModal && (
        <PermisoFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          permisoData={selectedPermiso}
          onSubmit={(data) =>
            selectedPermiso
              ? handleUpdate(selectedPermiso.permiso_id, data)
              : handleCreate(data)
          }
        />
      )}

      {/* PAGINATION */}
      <div className="pagination">
        <button onClick={prevPage} disabled={page === 1}>
          Prev
        </button>

        <span>
          Page {page} of {totalPages}
        </span>

        <button onClick={nextPage} disabled={page === totalPages}>
          Next
        </button>
      </div>
    </div>
  );
}

/* -------------------- SAME STYLES AS USERSPAGE -------------------- */

const modernStyles = `
.page-container {
  /* Llena el alto de la tarjeta del layout; solo el listado scrollea. */
  height: 100%;
  padding: 4px 0 0;
  font-family: 'Segoe UI';
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
/* Cabecera, alerta y paginacion conservan su alto; no se comprimen. */
.page-container > .header,
.page-container > .pagination,
.page-container > .omx-alert,
.page-container > .lista-recortada {
  flex: none;
}


.title {
  font-size: 26px;
  font-weight: 700;
  color: #2F4779;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
}

.search-input {
  padding: 10px 16px;
  width: 260px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E293B;
  font-size: 14px;
  transition: 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3B82F6;
  background: white;
}

.btn-create {
  padding: 10px 18px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.25s;
}

.btn-create:hover {
  background: #2563EB;
}

.table-card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);

  /* min-height: 0 permite encogerse por debajo del contenido; sin eso
     el scroll interno no ocurre. */
  flex: 0 1 auto;
  min-height: 0;
  overflow: auto;
  margin-top: 20px;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th {
  background: #F1F5F9;
  padding: 14px;
  font-size: 13px;
  color: #475569;
  text-align: left;
  border-bottom: 1px solid #E2E8F0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.modern-table td {
  padding: 14px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #F1F5F9;
}

.modern-table tr:hover td {
  background: #F8FAFC;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.action-icon {
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}

.action-icon.edit:hover {
  background: #DBEAFE;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #94A3B8;
  font-size: 14px;
}

.pagination {
  margin-top: 14px;
  margin-bottom: 2px;
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.pagination button {
  padding: 8px 14px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: .5;
}

.pagination span {
  font-size: 14px;
  color: #475569;
}
`;

export default PermisosPage;
